import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useEffect, useRef } from 'react';
import { BaseButton } from '../base/button/button';
import * as S from './drawer-sheet.styles';

export interface IDrawerSheetProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    /** An `@mdi/js` path for the square badge before the title. */
    iconPath?: string;
    /** Buttons under the title — Download, Open in new tab. */
    actions?: React.ReactNode;
    onClose: () => void;
    /** Read by screen readers as the drawer's name. Defaults to the title when it is a string. */
    ariaLabel?: string;
    className?: string;
    children?: React.ReactNode;
}

/**
 * A right-hand record drawer: the resizable `SidePanel`, a header (icon, title, subtitle, close,
 * actions) and a scrolling body. Fill the body with `DetailSection` / `DetailField`.
 *
 * Added for the File Cabinet (FE-225) so its drawer is not a third hand-built copy of the header the
 * Roster and Workflows drawers each build for themselves. Those two are left as they are.
 *
 * Not in `base/`: it is built on `SidePanel`, which lives in `messaging/`, and `base/` depends on no
 * feature folder. Put there, it closed an import cycle through the `base` barrel.
 */
export function DrawerSheet({
    title,
    subtitle,
    iconPath,
    actions,
    onClose,
    ariaLabel,
    className,
    children,
}: IDrawerSheetProps): React.ReactElement {
    const sheetRef = useRef<HTMLDivElement>(null);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    // Focus moves into the drawer when it opens and back to where it was when it closes, so a keyboard
    // or screen-reader user is told it appeared. Escape closes it.
    useEffect(() => {
        const previous = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
        sheetRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                closeRef.current();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return (): void => {
            document.removeEventListener('keydown', onKeyDown);
            previous?.focus();
        };
    }, []);

    return (
        <S.Panel className={className ? `fixed ${className}` : 'fixed'}>
            <S.Sheet
                ref={sheetRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="false"
                aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
            >
                <S.Header>
                    <S.HeaderRow>
                        {iconPath && (
                            <S.IconBox>
                                <Icon path={iconPath} size={0.85} />
                            </S.IconBox>
                        )}
                        <S.Titles>
                            <S.Title>{title}</S.Title>
                            {subtitle && <S.Subtitle>{subtitle}</S.Subtitle>}
                        </S.Titles>
                        <BaseButton type="button" variant="unstyled" aria-label="Close" onClick={onClose}>
                            <Icon path={mdiClose} size={0.9} />
                        </BaseButton>
                    </S.HeaderRow>
                    {actions && <S.Actions>{actions}</S.Actions>}
                </S.Header>
                <S.Body>{children}</S.Body>
            </S.Sheet>
        </S.Panel>
    );
}
